import React, { useState, useRef } from 'react';
import { optOuts } from '../../data/mockData';
import { useModal } from '../../context/ModalContext';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  CheckCircle2, ClipboardList, Download, BarChart3, TrendingUp, FileText,
  Ban, Calendar, Search, Eye, Save
} from 'lucide-react';

const OptOutManager = ({ showToast }) => {
  const [optOutList, setOptOutList] = useState(optOuts);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedOptOuts, setSelectedOptOuts] = useState([]);
  const { openModal, closeModal } = useModal();

  // Refs for settings
  const optOutMsgRef = useRef(null);
  const reSubscribeMsgRef = useRef(null);

  // Get unique groups for filter
  const uniqueGroups = [...new Set(optOutList.map(o => o.group))];

  // Filter opt-outs
  const filteredOptOuts = optOutList.filter(o => {
    const matchesSearch = o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          o.phone.includes(searchTerm) ||
                          o.reason.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGroup = !filterGroup || o.group === filterGroup;
    return matchesSearch && matchesGroup;
  });

  // Pagination
  const totalItems = filteredOptOuts.length;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOptOuts = filteredOptOuts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // Re-enable member (Re-opt in)
  const reenableMember = (member) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><CheckCircle2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Re-enable Member</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to re-enable <strong>{member.name}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--green-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--green)' 
          }}>
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will allow {member.name} to receive SMS messages again.
          </div>
          <div style={{ 
            marginTop: '12px',
            padding: '10px 12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)'
          }}>
            <strong>Member:</strong> {member.name}<br />
            <strong>Phone:</strong> {member.phone}<br />
            <strong>Opted out:</strong> {member.date}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              setOptOutList(optOutList.filter(o => o.name !== member.name || o.phone !== member.phone));
              setSelectedOptOuts(selectedOptOuts.filter(id => id !== member.name));
              showToast(`✅ ${member.name} re-opted in successfully!`, 'success');
              closeModal();
            }}
          >
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Re-enable Member
          </button>
        </div>
      </div>
    );
  };

  // Bulk re-enable
  const handleBulkReenable = () => {
    if (selectedOptOuts.length === 0) {
      showToast('Please select at least one member', 'error');
      return;
    }

    const selectedMembers = optOutList.filter(o => selectedOptOuts.includes(o.name));

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><CheckCircle2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Bulk Re-enable</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to re-enable <strong>{selectedOptOuts.length}</strong> members?
          </p>
          <div style={{ 
            maxHeight: '150px', 
            overflowY: 'auto',
            marginBottom: '12px'
          }}>
            {selectedMembers.map(m => (
              <div key={m.name} style={{ 
                padding: '6px 10px', 
                borderBottom: '1px solid var(--border-light)',
                fontSize: '13px'
              }}>
                {m.name} · {m.phone}
              </div>
            ))}
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--green-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--green)' 
          }}>
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will allow all selected members to receive SMS messages again.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              const namesToRemove = selectedOptOuts;
              setOptOutList(optOutList.filter(o => !namesToRemove.includes(o.name)));
              showToast(`✅ ${selectedOptOuts.length} members re-enabled!`, 'success');
              setSelectedOptOuts([]);
              closeModal();
            }}
          >
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Re-enable All
          </button>
        </div>
      </div>
    );
  };

  // View member details
  const handleViewDetails = (member) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><ClipboardList size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Member Details</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Name</span>
              <span style={{ fontWeight: '600' }}>{member.name}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Phone</span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{member.phone}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Group</span>
              <span>
                <span className="badge badge-navy" style={{ fontSize: '11px' }}>{member.group}</span>
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Opt-Out Date</span>
              <span>{member.date}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Reason</span>
              <span style={{ color: 'var(--text-mid)' }}>{member.reason}</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button className="btn btn-gold btn-sm" onClick={() => {
            closeModal();
            setTimeout(() => reenableMember(member), 100);
          }}>
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Re-enable
          </button>
        </div>
      </div>
    );
  };

  // Export opt-outs
  const handleExport = () => {
    if (filteredOptOuts.length === 0) {
      showToast('No opt-outs to export', 'error');
      return;
    }

    const fields = [
      { key: 'name', label: 'Member Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'group', label: 'Group' },
      { key: 'date', label: 'Opt-Out Date' },
      { key: 'reason', label: 'Reason' }
    ];

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Export Opt-Outs</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {filteredOptOuts.length} opt-out records.
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const formattedData = formatForExport(filteredOptOuts, fields);
                    const filename = getExportFilename('opt-outs');
                    
                    switch (format) {
                      case 'CSV':
                        exportToCSV(formattedData, filename);
                        break;
                      case 'JSON':
                        exportToJSON(formattedData, filename);
                        break;
                      case 'Excel':
                        exportToExcel(formattedData, filename);
                        break;
                      case 'PDF':
                        exportToPDF(formattedData, 'Opt-Outs Report', filename);
                        break;
                      default:
                        showToast('Export format not supported', 'error');
                        return;
                    }
                    showToast(`📥 ${filteredOptOuts.length} opt-outs exported as ${format}!`, 'success');
                    closeModal();
                  }}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ display: 'flex' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <ClipboardList size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ 
            padding: '10px 12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{filteredOptOuts.length} records will be exported</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // Save settings
  const handleSaveSettings = () => {
    const optOutMsg = optOutMsgRef.current?.value?.trim() || '';
    const reSubscribeMsg = reSubscribeMsgRef.current?.value?.trim() || '';

    if (!optOutMsg) {
      showToast('Please enter an opt-out confirmation message', 'error');
      return;
    }
    if (!reSubscribeMsg) {
      showToast('Please enter a re-subscribe confirmation message', 'error');
      return;
    }

    showToast('✅ Settings saved successfully!', 'success');
  };

  // Toggle select all
  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOptOuts(currentOptOuts.map(o => o.name));
    } else {
      setSelectedOptOuts([]);
    }
  };

  // Toggle select single
  const toggleSelect = (name) => {
    if (selectedOptOuts.includes(name)) {
      setSelectedOptOuts(selectedOptOuts.filter(n => n !== name));
    } else {
      setSelectedOptOuts([...selectedOptOuts, name]);
    }
  };

  const stats = {
    total: optOutList.length,
    thisMonth: optOutList.filter(o => {
      const date = new Date(o.date);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length,
    reopted: 7,
    rate: (optOutList.length / 1247 * 100).toFixed(1)
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          Manage members who have opted out of SMS messages. Fully compliant with data regulations.
          {selectedOptOuts.length > 0 && (
            <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
              · {selectedOptOuts.length} selected
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {selectedOptOuts.length > 0 && (
            <button className="btn btn-gold btn-sm" onClick={handleBulkReenable}>
              <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Re-enable Selected
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={handleExport}>
            <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold">
          <div className="stat-label">Total Opted Out</div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-change down">{stats.rate}%</div>
          <div className="stat-icon"><Ban size={20} /></div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">This Month</div>
          <div className="stat-value">{stats.thisMonth}</div>
          <div className="stat-icon"><Calendar size={20} /></div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Re-Opted In</div>
          <div className="stat-value">{stats.reopted}</div>
          <div className="stat-change up">↑ came back</div>
          <div className="stat-icon"><CheckCircle2 size={20} /></div>
        </div>
        <div className="stat-card purple">
          <div className="stat-label">Opt-Out Rate</div>
          <div className="stat-value">{stats.rate}%</div>
          <div className="stat-icon"><BarChart3 size={20} /></div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <div className="card-title">Opt-Out List</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <div className="search-box" style={{ maxWidth: '200px' }}>
              <span className="search-icon"><Search size={16} /></span>
              <input 
                type="text" 
                placeholder="Search..." 
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <select 
              className="form-select" 
              style={{ width: '140px', padding: '6px 10px', fontSize: '12px' }}
              value={filterGroup}
              onChange={(e) => {
                setFilterGroup(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Groups</option>
              {uniqueGroups.map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            <select 
              className="form-select" 
              style={{ width: 'auto', padding: '6px 10px', fontSize: '12px' }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: '30px' }}>
                  <input 
                    type="checkbox" 
                    checked={selectedOptOuts.length === currentOptOuts.length && currentOptOuts.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Member</th>
                <th>Phone</th>
                <th>Group</th>
                <th>Date</th>
                <th>Reason</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentOptOuts.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
                    <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><CheckCircle2 size={40} strokeWidth={1.5} /></div>
                    <div>No opt-outs found</div>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      Try adjusting your filters or search term
                    </div>
                  </td>
                </tr>
              ) : (
                currentOptOuts.map(o => (
                  <tr key={o.name}>
                    <td>
                      <input 
                        type="checkbox" 
                        checked={selectedOptOuts.includes(o.name)}
                        onChange={() => toggleSelect(o.name)}
                      />
                    </td>
                    <td style={{ fontWeight: '500' }}>{o.name}</td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}>{o.phone}</td>
                    <td>
                      <span className="badge badge-navy" style={{ fontSize: '10px', background: 'var(--navy)', color: 'var(--gold-light)', padding: '2px 10px', borderRadius: '12px' }}>
                        {o.group}
                      </span>
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--text-light)' }}>{o.date}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-mid)', maxWidth: '150px' }}>{o.reason}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button 
                          className="btn btn-ghost btn-sm btn-icon" 
                          onClick={() => handleViewDetails(o)}
                          title="View Details"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => reenableMember(o)}
                          style={{ color: 'var(--green)', fontSize: '11px' }}
                        >
                          <CheckCircle2 size={13} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Re-enable
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '14px 0 0', 
            borderTop: '1px solid var(--border)', 
            marginTop: '4px',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} opt-outs
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                ← Prev
              </button>
              {getPageNumbers().map(page => (
                <button 
                  key={page}
                  className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ))}
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title" style={{ marginBottom: '14px' }}>Opt-Out Settings</div>
        <div className="grid-2">
          <div>
            <div style={{ background: 'var(--cream)', borderRadius: '10px', padding: '14px', marginBottom: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)', marginBottom: '4px' }}>To Opt Out</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '18px', color: 'var(--red)', fontWeight: '600' }}>STOP → 40404</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>Also: UNSUBSCRIBE, QUIT, CANCEL</div>
            </div>
            <div style={{ background: 'var(--cream)', borderRadius: '10px', padding: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)', marginBottom: '4px' }}>To Re-Subscribe</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '18px', color: 'var(--green)', fontWeight: '600' }}>START → 40404</div>
            </div>
          </div>
          <div>
            <div className="form-group">
              <label className="form-label">Opt-Out Confirmation SMS</label>
              <textarea 
                className="form-textarea" 
                rows="2" 
                ref={optOutMsgRef}
                defaultValue="You have been unsubscribed from Grace Chapel SMS. To re-subscribe, text START to 40404." 
              />
            </div>
            <div className="form-group">
              <label className="form-label">Re-Subscribe Confirmation</label>
              <textarea 
                className="form-textarea" 
                rows="2" 
                ref={reSubscribeMsgRef}
                defaultValue="Welcome back, {FirstName}! You are now re-subscribed. God bless you! 🙏 — Grace Chapel" 
              />
            </div>
            <button className="btn btn-gold btn-sm" onClick={handleSaveSettings}>
<Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Settings
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .stat-change {
          font-size: 12px;
          font-weight: 600;
          margin-top: 2px;
        }
        .stat-change.up {
          color: var(--green);
        }
        .stat-change.down {
          color: var(--red);
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .btn-primary {
          background: var(--gold);
          color: var(--navy);
          border: none;
        }
        .btn-primary:hover {
          background: var(--gold-dark);
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .table-wrap {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          text-align: left;
          padding: 10px 12px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-light);
          border-bottom: 2px solid var(--border);
        }
        td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-light);
          font-size: 13px;
        }
        tr:hover td {
          background: var(--cream);
        }
        input[type="checkbox"] {
          cursor: pointer;
          width: 16px;
          height: 16px;
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
          }
        }
      `}</style>
    </>
  );
};

export default OptOutManager;